import { Skeleton } from '@vnetwork/ui/primitives';
import { PieChart } from '@vnetwork/ui/charts';
import { EmptyState } from '@vnetwork/ui/data';
import { PieChart as PieIcon } from 'lucide-react';
import { WidgetCard } from '@/pages/dashboard/widgets/WidgetCard';
import { CATEGORY_LABEL } from '@/pages/operations/detail/tabs/ops-detail-shared';
import type { OpsCategory } from '@/api/operations';

/** Muted Material 400 palette per ui-conventions (feedback_chart_palette_muted_material). */
const CATEGORY_COLORS: Record<string, string> = {
  cdn: 'var(--vn-info-solid)',      // blue-ish
  waap: 'var(--vn-warning-solid)',  // orange-ish
  compute: 'var(--vn-success-solid)', // green-ish
  storage: 'var(--vn-accent-solid)',  // purple-ish
  media: 'var(--vn-primary-solid)',   // primary
  registrar: 'var(--vn-fg-muted)',
  other: 'var(--vn-border-default)',
};

interface Props {
  byCategory: Record<string, Record<string, number>> | undefined;
  isLoading: boolean;
}

/**
 * Category breakdown donut — active services by category.
 * Uses @vnetwork/ui PieChart with donut=true. Only active-status counts are
 * shown (terminated/pending omitted from the donut to keep it meaningful).
 */
export function OpsCategoryBreakdownWidget({ byCategory, isLoading }: Props) {
  const data = Object.entries(byCategory ?? {})
    .map(([cat, statuses]) => ({
      name: CATEGORY_LABEL[cat as OpsCategory] ?? cat,
      value: statuses['active'] ?? 0,
      color: CATEGORY_COLORS[cat] ?? 'var(--vn-fg-muted)',
    }))
    .filter((d) => d.value > 0)
    .sort((a, b) => b.value - a.value);

  const total = data.reduce((s, d) => s + d.value, 0);

  return (
    <WidgetCard
      title="Active services by category"
      description="Active-status only"
    >
      {isLoading ? (
        <Skeleton style={{ height: 200 }} />
      ) : data.length === 0 ? (
        <EmptyState
          icon={<PieIcon size={20} />}
          title="No active services"
          description="No active services found across any category."
        />
      ) : (
        <PieChart
          data={data}
          height={200}
          donut
          showLegend
          centerLabel={
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  fontSize: 'var(--vn-font-size-xl)',
                  fontWeight: 700,
                  color: 'var(--vn-fg-default)',
                  lineHeight: 1.2,
                }}
              >
                {total.toLocaleString('en-US')}
              </div>
              <div
                style={{
                  fontSize: 'var(--vn-font-size-xs)',
                  color: 'var(--vn-fg-muted)',
                }}
              >
                active
              </div>
            </div>
          }
          valueFormatter={(v) => `${v} services`}
        />
      )}
    </WidgetCard>
  );
}
